import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { selectUser } from "../../../../store/slices/authentication";
import { useGetMarketplaceItemsQuery } from "../../../../store/slices/api";
import {
  SharedLoading,
  SharedFeatureError,
  SharedCollapsibleIntro,
} from "../../../shared";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import ItemCard from "./ItemCard";
import ItemDetail from "./ItemDetail";
import { CATEGORIES, categoryName } from "./itemFormat";

const ALL = "All";

function MarketplaceItems() {
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;
  const { data: items, isLoading, isError, refetch } =
    useGetMarketplaceItemsQuery();
  const [selectedCategory, setSelectedCategory] = useState(ALL);
  const [showSold, setShowSold] = useState(false);

  // The open listing lives in the address (?item=<id>, next to ?tab=), so the
  // phone's Back button closes it and a link to a listing opens it.
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const openItemId = searchParams.get("item");
  const openedHere = location.state?.marketplaceItemOpened === true;

  const openItem = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      next.set("item", String(id));
      setSearchParams(next, { state: { marketplaceItemOpened: true } });
    },
    [searchParams, setSearchParams]
  );

  // Opened from the grid: step back, so Back after Close doesn't reopen it.
  // Arrived by a link: drop ?item= in place instead of leaving the site.
  const closeItem = useCallback(() => {
    if (openedHere) {
      navigate(-1);
    } else {
      const next = new URLSearchParams(searchParams);
      next.delete("item");
      setSearchParams(next, { replace: true });
    }
  }, [openedHere, navigate, searchParams, setSearchParams]);

  // The empty Marketplace points to the Post Item tab that already exists.
  const goToPostItem = () => setSearchParams({ tab: "createItem" }, { replace: true });

  // How many listings each chip would show, respecting "Show sold items".
  const counts = useMemo(() => {
    const tally = { [ALL]: 0 };
    (items || []).forEach((item) => {
      if (!showSold && item.is_sold) return;
      tally[ALL] += 1;
      const key = CATEGORIES.includes(item.category) ? item.category : "other";
      tally[key] = (tally[key] || 0) + 1;
    });
    return tally;
  }, [items, showSold]);

  // What an empty page says. Each hint must lead to something real when
  // followed (independent review, 2026-09-27):
  //   - no listings at all                       -> "No listings yet"
  //   - sold hidden, nothing unsold anywhere     -> tick "Show sold items"
  //       (and choose "All items" if the picked category never had any)
  //   - sold hidden, picked category only sold   -> tick "Show sold items",
  //       or choose "All items" for what's for sale
  //   - picked category never had any            -> choose "All items"
  const categoryOf = (item) => (CATEGORIES.includes(item.category) ? item.category : "other");
  const emptyState = (() => {
    const all = items || [];
    if (all.length === 0) {
      return {
        heading: "No listings yet",
        hint: "Heirlooms, rentals and tickets from the family will appear here.",
      };
    }
    const inPicked = selectedCategory === ALL ? all : all.filter((i) => categoryOf(i) === selectedCategory);
    const unsoldAnywhere = all.some((i) => !i.is_sold);
    const pickedName = categoryName(selectedCategory);
    if (!showSold && !unsoldAnywhere) {
      return {
        heading: "Nothing for sale right now",
        hint:
          inPicked.length > 0
            ? "Everything listed has been sold. Tick \u201cShow sold items\u201d to see past listings."
            : "Everything listed has been sold. Tick \u201cShow sold items\u201d and choose \u201cAll items\u201d to see past listings.",
      };
    }
    if (!showSold && inPicked.length > 0) {
      return {
        heading: "Nothing for sale in this category",
        hint: `Everything in ${pickedName} has been sold. Tick \u201cShow sold items\u201d to see it, or choose \u201cAll items\u201d to see what\u2019s for sale.`,
      };
    }
    return {
      heading: "No listings in this category",
      hint: `Nothing in ${pickedName} right now. Choose \u201cAll items\u201d to see ${showSold ? "everything" : "what\u2019s for sale"}.`,
    };
  })();

  // Gate on isLoading (first load) — not isFetching — so background refreshes
  // don't blank the list. Show a friendly retry if the request actually fails.
  if (isLoading) {
    return <SharedLoading text="Loading items..." />;
  }
  if (isError || !items) {
    return <SharedFeatureError onRetry={refetch} />;
  }

  const filteredItems = [...items]
    .filter((item) => {
      if (selectedCategory !== ALL) {
        const key = CATEGORIES.includes(item.category) ? item.category : "other";
        if (key !== selectedCategory) return false;
      }
      if (!showSold && item.is_sold) return false;
      return true;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // The open listing is looked up in ALL items, not just the filtered ones, so
  // marking it sold does not snap it shut. An unknown or deleted id just
  // shows the grid.
  const shownItem =
    openItemId !== null ? items.find((i) => String(i.id) === openItemId) : null;

  return (
    <div className="flex flex-col gap-y-[28px]">
      {/* The former "Landing" tab's copy, now collapsed above the live listings */}
      <SharedCollapsibleIntro
        heading="A Private Marketplace for the Family"
        teaser="Heirlooms, rentals, tickets, and cousin commerce. Have something to offer? Head over to the Post Item tab."
      >
        <p className="text-[17px] font-body text-dupontBlue dark:text-darkText leading-relaxed">
          Ever wonder why on earth you are still paying for a storage unit
          keeping grandmothers' hutches and wardrobes? Perhaps your garage is
          full of chairs that need a new home. I know many of you have mentioned
          that this would be a feature that a family website should include. Well
          with this family app we are hoping to enable a private marketplace for
          family heirlooms that could take on new life traveling from one to
          another.
        </p>
        <p className="text-[17px] font-body text-dupontBlue dark:text-darkText leading-relaxed">
          My first apartment out of college had my grandmothers drop leaflet
          table and my great aunt's chairs. So, if you have lost the utility and
          perhaps the emotional grip of the memory, pass it on and let it become
          a part of someone else's history!
        </p>
        <p className="text-[17px] font-body text-dupontBlue dark:text-darkText leading-relaxed">
          But of course, this tab is not only limited to the wonderful past. It
          can be to enjoy the exciting future! Perhaps it is a beach house that
          instead of Airbnb why not let a cousin come down and stay. Perhaps
          there is a donation sponsoring someone as they do a marathon or a
          great arbitrage opportunity. What about buying tickets, paid through
          the seller's own Venmo, Cash App, PayPal or Zelle! It truly can become a creative place
          for a litany of cousin commerce and connection opportunities.
        </p>
      </SharedCollapsibleIntro>

      {/* Filter row: a quiet single line, no box of its own, lined up with the
          grid (Sam, 2026-09-27: "too many boxes inside boxes"). Always shown,
          even with no listings yet, so people can see how the Marketplace is
          organised (Sam, 2026-09-27). */}
      <div className="mt-[8px] flex flex-row flex-wrap items-center gap-x-[24px] gap-y-[12px]">
        <Autocomplete
          options={[ALL, ...CATEGORIES]}
          value={selectedCategory}
          onChange={(event, newValue) => setSelectedCategory(newValue || ALL)}
          getOptionLabel={(cat) =>
            (cat === ALL ? "All items" : categoryName(cat)) +
            (counts[cat] ? ` (${counts[cat]})` : "")
          }
          disableClearable
          size="small"
          sx={{ minWidth: 220 }}
          renderInput={(params) => (
            <TextField {...params} label="Category" variant="outlined" />
          )}
        />
        <label className="flex items-center gap-x-[8px] text-[15px] font-body text-dupontBlue dark:text-darkText cursor-pointer">
          <input
            type="checkbox"
            checked={showSold}
            onChange={(e) => setShowSold(e.target.checked)}
            className="w-[18px] h-[18px] accent-dupontGold"
          />
          Show sold items
        </label>
        <span className="ml-auto text-[14px] font-body text-dupontGray dark:text-darkTextSecondary">
          {filteredItems.length} {filteredItems.length === 1 ? "item" : "items"}
        </span>
      </div>

      {/* Items grid */}
      {filteredItems.length > 0 ? (
        // 1 column on phones, 2 on tablets, 3 on desktop. From 768px the sidebar
        // takes 300px, so the grid drops back to 1 column until there is room.
        <div className="grid grid-cols-1 min-[560px]:grid-cols-2 md:grid-cols-1 min-[800px]:grid-cols-2 xl:grid-cols-3 gap-[20px] sm:gap-[28px]">
          {filteredItems.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              isOwner={Boolean(individualId && item.individual_id === individualId)}
              onOpen={openItem}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center text-center py-[56px] px-[16px] gap-y-[10px]">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-dupontGold/70 dark:text-darkGold/60 mb-[6px]">
            <path d="M3 10.5V4.5a1.5 1.5 0 0 1 1.5-1.5h6l10 10-7.5 7.5-10-10z" />
            <circle cx="7.5" cy="7.5" r="1.4" />
          </svg>
          <h3 className="text-[22px] font-display font-bold text-dupontBlue dark:text-darkText">
            {emptyState.heading}
          </h3>
          <p className="text-[16px] font-body text-dupontGray dark:text-darkTextSecondary leading-relaxed max-w-[420px]">
            {/* Each hint names the control that will actually show something
                (see emptyState above). */}
            {emptyState.hint}
          </p>
          {items.length === 0 && (
            <button
              type="button"
              onClick={goToPostItem}
              className="mt-[12px] rounded-full px-[22px] py-[11px] bg-dupontGold hover:bg-dupontGoldDk text-white text-[15px] font-medium transition-colors duration-200 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dupontBlue dark:focus-visible:outline-darkGold"
            >
              Post an item
            </button>
          )}
        </div>
      )}

      {shownItem && (
        <ItemDetail item={shownItem} individualId={individualId} onClose={closeItem} />
      )}
    </div>
  );
}

export default MarketplaceItems;
